import { useState } from "react";
import { api } from "../api/client.js";
import { resolveProductMediaUrl } from "../data/products.js";
import { getAdminToken } from "./auth.js";

const EMPTY_PRODUCT = {
  slug: "",
  name: "",
  collectionLabel: "",
  description: "",
  productType: "balloon",
  fragranceNote: "",
  colorName: "",
  colorValue: "#c9a96e",
  secondColorName: "",
  secondColorValue: "#c9a96e",
  price: "",
  currency: "USD",
  mood: "",
  packageWeight: "",
  packageLength: "",
  packageWidth: "",
  packageHeight: "",
  imageUrl: "",
  alt: "",
  featured: false,
  inStock: true,
  media: [],
};

const EMPTY_PHOTO = {
  id: "",
  type: "image",
  url: "",
  alt: "",
};

const EMPTY_VIDEO = {
  id: "",
  type: "video",
  url: "",
  alt: "",
};

const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const ACCEPTED_VIDEO_TYPES = ["video/mp4", "video/webm"];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_VIDEO_BYTES = 500 * 1024 * 1024;
const MAX_ADDITIONAL_PHOTOS = 2;

const slugify = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const toFormProduct = (product) => ({
  slug: product.id,
  name: product.name,
  collectionLabel: product.collectionLabel || "",
  description: product.description,
  productType: product.productType || "balloon",
  fragranceNote: product.fragranceNote || "",
  colorName: product.color.name,
  colorValue: product.color.value,
  secondColorName: product.secondColor?.name || "",
  secondColorValue: product.secondColor?.value || "#c9a96e",
  price: String(product.price),
  currency: product.currency,
  mood: product.mood,
  packageWeight: product.packageWeight ? String(product.packageWeight) : "",
  packageLength: product.packageLength ? String(product.packageLength) : "",
  packageWidth: product.packageWidth ? String(product.packageWidth) : "",
  packageHeight: product.packageHeight ? String(product.packageHeight) : "",
  imageUrl: product.imageUrl,
  alt: product.alt,
  featured: product.featured,
  inStock: product.inStock !== false,
  media: (product.media || [])
    .filter((media) => ["image", "video"].includes(media.type))
    .filter((media) => media.type === "video" || media.url !== product.imageUrl)
    .map((media) => ({
      id: media.id,
      type: media.type,
      url: media.url,
      alt: media.alt || "",
    })),
});

const toApiProduct = (form) => {
  const product = {
    slug: form.slug.trim().toLowerCase(),
    name: form.name.trim(),
    collectionLabel: form.collectionLabel.trim(),
    description: form.description.trim(),
    productType: form.productType === "candle" ? "candle" : "balloon",
    fragranceNote: form.fragranceNote.trim(),
    color: {
      name: form.colorName.trim(),
      value: form.colorValue.toLowerCase(),
    },
    price: Number(form.price),
    currency: form.currency.trim().toUpperCase(),
    mood: form.mood.trim(),
    packageWeight: Number(form.packageWeight),
    packageLength: Number(form.packageLength),
    packageWidth: Number(form.packageWidth),
    packageHeight: Number(form.packageHeight),
    imageUrl: form.imageUrl.trim(),
    alt: form.alt.trim(),
    featured: form.featured,
    inStock: form.inStock,
    media: form.media
      .filter((media) => media.url.trim())
      .map((media, index) => ({
        id:
          slugify(media.id) ||
          `${form.slug.trim().toLowerCase()}-media-${index + 1}`,
        type: media.type,
        url: media.url.trim(),
        alt: media.alt.trim(),
      })),
  };

  if (form.secondColorName.trim()) {
    product.secondColor = {
      name: form.secondColorName.trim(),
      value: form.secondColorValue.toLowerCase(),
    };
  }

  return product;
};

const validateImageFile = (file) => {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    throw new Error("Choose a JPEG, PNG, or WebP image.");
  }
  if (file.size > MAX_IMAGE_BYTES) {
    throw new Error("The image cannot exceed 5 MB.");
  }
};

const validateVideoFile = (file) => {
  if (!ACCEPTED_VIDEO_TYPES.includes(file.type)) {
    throw new Error("Choose an MP4 or WebM video.");
  }
  if (file.size > MAX_VIDEO_BYTES) {
    throw new Error("The video cannot exceed 500 MB.");
  }
};

const imageAltFromFilename = (filename) =>
  filename
    .replace(/\.[^.]+$/, "")
    .replace(/[-_]+/g, " ")
    .trim();

const getErrorMessage = (error) => {
  if (Array.isArray(error.details) && error.details.length > 0) {
    return error.details
      .map((detail) => `${detail.field}: ${detail.message}`)
      .join(" · ");
  }

  return error.message || "The product could not be saved";
};

const validatePackageDetails = (form) => {
  const fields = [
    ["packageWeight", "Package weight"],
    ["packageLength", "Package length"],
    ["packageWidth", "Package width"],
    ["packageHeight", "Package height"],
  ];

  fields.forEach(([field, label]) => {
    const value = Number(form[field]);
    if (!form[field] || !Number.isFinite(value) || value <= 0) {
      throw new Error(`${label} must be greater than 0.`);
    }
  });
};

function ProductManager({ products, onChanged, onUnauthorized }) {
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [editingSlug, setEditingSlug] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingMain, setUploadingMain] = useState(false);
  const [uploadingMediaIndex, setUploadingMediaIndex] = useState(null);
  const [deletingSlug, setDeletingSlug] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const updateField = (field, value) => {
    setError("");
    setNotice("");
    setForm((current) => ({ ...current, [field]: value }));
  };

  const startCreate = () => {
    setForm(EMPTY_PRODUCT);
    setEditingSlug(null);
    setError("");
    setNotice("");
    setIsFormOpen(true);
  };

  const startEdit = (product) => {
    setForm(toFormProduct(product));
    setEditingSlug(product.id);
    setError("");
    setNotice("");
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setForm(EMPTY_PRODUCT);
    setEditingSlug(null);
    setError("");
    setIsFormOpen(false);
  };

  const addPhoto = () => {
    setForm((current) => ({
      ...current,
      media: [...current.media, { ...EMPTY_PHOTO }],
    }));
  };

  const addVideo = () => {
    setForm((current) => ({
      ...current,
      media: [...current.media, { ...EMPTY_VIDEO }],
    }));
  };

  const updateMedia = (index, field, value) => {
    setForm((current) => ({
      ...current,
      media: current.media.map((media, mediaIndex) =>
        mediaIndex === index ? { ...media, [field]: value } : media,
      ),
    }));
  };

  const removeMedia = (index) => {
    setForm((current) => ({
      ...current,
      media: current.media.filter((_, mediaIndex) => mediaIndex !== index),
    }));
  };

  const uploadImage = async (file) => {
    validateImageFile(file);
    return api.admin.images.upload(file, getAdminToken());
  };

  const uploadVideo = async (file) => {
    validateVideoFile(file);
    return api.admin.videos.upload(file, getAdminToken());
  };

  const handleMainImageUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setUploadingMain(true);
    setError("");
    setNotice("");

    try {
      const response = await uploadImage(file);
      setForm((current) => ({
        ...current,
        imageUrl: response.data.url,
        alt: current.alt || imageAltFromFilename(file.name),
      }));
      setNotice("Main image uploaded.");
    } catch (requestError) {
      handleRequestError(requestError);
    } finally {
      setUploadingMain(false);
    }
  };

  const handleMediaImageUpload = async (index, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setUploadingMediaIndex(index);
    setError("");
    setNotice("");

    try {
      const response = await uploadImage(file);
      setForm((current) => ({
        ...current,
        media: current.media.map((media, mediaIndex) =>
          mediaIndex === index
            ? {
                ...media,
                id:
                  media.id || `${current.slug || "product"}-photo-${index + 1}`,
                url: response.data.url,
                alt: media.alt || imageAltFromFilename(file.name),
              }
            : media,
        ),
      }));
      setNotice("Photo uploaded.");
    } catch (requestError) {
      handleRequestError(requestError);
    } finally {
      setUploadingMediaIndex(null);
    }
  };

  const handleVideoUpload = async (index, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setUploadingMediaIndex(index);
    setError("");
    setNotice("");

    try {
      const response = await uploadVideo(file);
      setForm((current) => ({
        ...current,
        media: current.media.map((media, mediaIndex) =>
          mediaIndex === index
            ? {
                ...media,
                id: media.id || `${current.slug || "product"}-video`,
                url: response.data.url,
                alt: media.alt || imageAltFromFilename(file.name),
              }
            : media,
        ),
      }));
      setNotice("Video uploaded.");
    } catch (requestError) {
      handleRequestError(requestError);
    } finally {
      setUploadingMediaIndex(null);
    }
  };

  const handleRequestError = (requestError) => {
    if (requestError.status === 401 || requestError.status === 403) {
      onUnauthorized();
      return;
    }
    setError(getErrorMessage(requestError));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setNotice("");

    try {
      validatePackageDetails(form);
      if (!form.imageUrl) {
        throw new Error("Upload a main product image before saving.");
      }
      if (
        form.secondColorValue !== EMPTY_PRODUCT.secondColorValue &&
        !form.secondColorName.trim()
      ) {
        throw new Error("Enter a name for the second color before saving.");
      }

      const token = getAdminToken();
      const product = toApiProduct(form);

      if (editingSlug) {
        await api.admin.products.update(editingSlug, product, token);
        setNotice("Product updated successfully.");
      } else {
        await api.admin.products.create(product, token);
        setNotice("Product created successfully.");
      }

      setForm(EMPTY_PRODUCT);
      setEditingSlug(null);
      setIsFormOpen(false);
      await onChanged();
    } catch (requestError) {
      handleRequestError(requestError);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product) => {
    const confirmed = window.confirm(
      `Delete “${product.name}”? This cannot be undone.`,
    );
    if (!confirmed) return;

    setDeletingSlug(product.id);
    setError("");
    setNotice("");

    try {
      await api.admin.products.remove(product.id, getAdminToken());
      setNotice(`${product.name} was deleted.`);
      if (editingSlug === product.id) closeForm();
      await onChanged();
    } catch (requestError) {
      handleRequestError(requestError);
    } finally {
      setDeletingSlug(null);
    }
  };

  const additionalPhotoCount = form.media.filter(
    (media) => media.type === "image",
  ).length;
  const hasVideo = form.media.some((media) => media.type === "video");

  return (
    <section className="admin-panel admin-products-panel">
      <header className="admin-panel-heading">
        <div>
          <p className="admin-eyebrow">Catalog management</p>
          <h2>Products</h2>
        </div>
        <button type="button" onClick={startCreate}>
          Add product
        </button>
      </header>

      {notice && (
        <p className="admin-form-notice" role="status">
          {notice}
        </p>
      )}
      {error && (
        <p className="admin-form-error" role="alert">
          {error}
        </p>
      )}

      {isFormOpen && (
        <form className="admin-product-form" onSubmit={handleSubmit}>
          <header>
            <div>
              <h3>{editingSlug ? "Edit product" : "Add a new product"}</h3>
              <p>Fields marked with * are required.</p>
            </div>
            <button
              type="button"
              className="admin-secondary"
              onClick={closeForm}
            >
              Cancel
            </button>
          </header>

          <div className="admin-form-grid">
            <label>
              <span>Product name *</span>
              <input
                value={form.name}
                onChange={(event) => {
                  const name = event.target.value;
                  setForm((current) => ({
                    ...current,
                    name,
                    ...(!editingSlug && !current.slug
                      ? { slug: slugify(name) }
                      : {}),
                  }));
                }}
                maxLength="120"
                required
              />
            </label>

            <label>
              <span>Slug / product ID *</span>
              <input
                value={form.slug}
                onChange={(event) =>
                  updateField("slug", slugify(event.target.value))
                }
                placeholder="example-product"
                pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                required
              />
            </label>

            <label>
              <span>Product type *</span>
              <select
                value={form.productType}
                onChange={(event) =>
                  updateField("productType", event.target.value)
                }
                required
              >
                <option value="balloon">Balloon</option>
                <option value="candle">Candle set</option>
              </select>
            </label>

            <label>
              <span>Collection label</span>
              <input
                value={form.collectionLabel}
                onChange={(event) =>
                  updateField("collectionLabel", event.target.value)
                }
                maxLength="80"
                placeholder="STATE I, Limited edition, New arrival"
              />
            </label>

            <label>
              <span>Price *</span>
              <input
                type="number"
                min="0"
                max="10000000"
                step="0.01"
                value={form.price}
                onChange={(event) => updateField("price", event.target.value)}
                required
              />
            </label>

            <label>
              <span>Currency *</span>
              <input
                value={form.currency}
                onChange={(event) =>
                  updateField("currency", event.target.value.toUpperCase())
                }
                minLength="3"
                maxLength="3"
                pattern="[A-Za-z]{3}"
                required
              />
            </label>

            <label>
              <span>Mood *</span>
              <input
                value={form.mood}
                onChange={(event) => updateField("mood", event.target.value)}
                maxLength="200"
                placeholder="Soft · Dreamy · Comforting"
                required
              />
            </label>

            <label>
              <span>Package weight, lb *</span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={form.packageWeight}
                onChange={(event) =>
                  updateField("packageWeight", event.target.value)
                }
                required
              />
            </label>

            <label>
              <span>Package length, in *</span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={form.packageLength}
                onChange={(event) =>
                  updateField("packageLength", event.target.value)
                }
                required
              />
            </label>

            <label>
              <span>Package width, in *</span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={form.packageWidth}
                onChange={(event) =>
                  updateField("packageWidth", event.target.value)
                }
                required
              />
            </label>

            <label>
              <span>Package height, in *</span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={form.packageHeight}
                onChange={(event) =>
                  updateField("packageHeight", event.target.value)
                }
                required
              />
            </label>

            <label>
              <span>Fragrance note</span>
              <input
                value={form.fragranceNote}
                onChange={(event) =>
                  updateField("fragranceNote", event.target.value)
                }
                maxLength="300"
              />
            </label>

            <label>
              <span>Color name *</span>
              <input
                value={form.colorName}
                onChange={(event) =>
                  updateField("colorName", event.target.value)
                }
                maxLength="80"
                required
              />
            </label>

            <label>
              <span>Color *</span>
              <span className="admin-color-input">
                <input
                  type="color"
                  value={form.colorValue}
                  onChange={(event) =>
                    updateField("colorValue", event.target.value)
                  }
                  required
                />
                <input
                  value={form.colorValue}
                  onChange={(event) =>
                    updateField("colorValue", event.target.value)
                  }
                  pattern="#[0-9A-Fa-f]{6}"
                  required
                />
              </span>
            </label>

            <label>
              <span>Second color name</span>
              <input
                value={form.secondColorName}
                onChange={(event) =>
                  updateField("secondColorName", event.target.value)
                }
                maxLength="80"
                placeholder="Required if adding a second color"
              />
            </label>

            <label>
              <span>Second color</span>
              <span className="admin-color-input">
                <input
                  type="color"
                  value={form.secondColorValue}
                  onChange={(event) =>
                    updateField("secondColorValue", event.target.value)
                  }
                />
                <input
                  value={form.secondColorValue}
                  onChange={(event) =>
                    updateField("secondColorValue", event.target.value)
                  }
                  pattern="#[0-9A-Fa-f]{6}"
                />
              </span>
            </label>
          </div>

          <label>
            <span>Description *</span>
            <textarea
              value={form.description}
              onChange={(event) =>
                updateField("description", event.target.value)
              }
              maxLength="3000"
              rows="5"
              required
            />
          </label>

          <div className="admin-form-grid admin-image-fields">
            <label>
              <span>Main product image *</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleMainImageUpload}
                disabled={uploadingMain}
              />
              <small>
                {uploadingMain
                  ? "Uploading…"
                  : "JPEG, PNG, or WebP · maximum 5 MB"}
              </small>
            </label>

            <label>
              <span>Main image description *</span>
              <input
                value={form.alt}
                onChange={(event) => updateField("alt", event.target.value)}
                maxLength="300"
                placeholder="Describe the product image"
                required
              />
            </label>
          </div>

          {form.imageUrl && (
            <div className="admin-main-image-preview">
              <span>Image preview</span>
              <img
                src={resolveProductMediaUrl(form.imageUrl)}
                alt={form.alt || "Product preview"}
              />
            </div>
          )}

          <section className="admin-media-editor">
            <header>
              <div>
                <h3>Product gallery</h3>
                <p>
                  Add up to two more photos (three total) and one optional
                  video.
                </p>
              </div>
              <div className="admin-media-actions">
                <button
                  type="button"
                  className="admin-secondary"
                  onClick={addPhoto}
                  disabled={additionalPhotoCount >= MAX_ADDITIONAL_PHOTOS}
                >
                  Add photo
                </button>
                {/* <button
                  type="button"
                  className="admin-secondary"
                  onClick={addVideo}
                  disabled={hasVideo}
                >
                  Add video
                </button> */}
              </div>
            </header>

            {form.media.map((media, index) => (
              <fieldset key={index}>
                <legend>
                  {media.type === "video"
                    ? "Product video"
                    : "Additional photo"}
                </legend>
                <div className="admin-form-grid">
                  <label>
                    <span>Media ID</span>
                    <input
                      value={media.id}
                      onChange={(event) =>
                        updateMedia(index, "id", slugify(event.target.value))
                      }
                      placeholder={`${form.slug || "product"}-detail-${index + 1}`}
                    />
                  </label>

                  <label>
                    <span>
                      {media.type === "video" ? "Video file" : "Image file"} *
                    </span>
                    <input
                      type="file"
                      accept={
                        media.type === "video"
                          ? "video/mp4,video/webm"
                          : "image/jpeg,image/png,image/webp"
                      }
                      onChange={(event) =>
                        media.type === "video"
                          ? handleVideoUpload(index, event)
                          : handleMediaImageUpload(index, event)
                      }
                      disabled={uploadingMediaIndex === index}
                    />
                    <small>
                      {uploadingMediaIndex === index
                        ? "Uploading…"
                        : media.url
                          ? `${media.type === "video" ? "Video" : "Photo"} uploaded`
                          : media.type === "video"
                            ? "MP4 or WebM · maximum 500 MB"
                            : "JPEG, PNG, or WebP · maximum 5 MB"}
                    </small>
                  </label>

                  <label>
                    <span>
                      {media.type === "video"
                        ? "Video description"
                        : "Photo description"}
                    </span>
                    <input
                      value={media.alt}
                      onChange={(event) =>
                        updateMedia(index, "alt", event.target.value)
                      }
                      maxLength="300"
                    />
                  </label>
                </div>
                {media.url && media.type === "video" && (
                  <video
                    className="admin-media-preview"
                    src={resolveProductMediaUrl(media.url)}
                    controls
                    playsInline
                    preload="metadata"
                  />
                )}
                {media.url && media.type === "image" && (
                  <img
                    className="admin-media-preview"
                    src={resolveProductMediaUrl(media.url)}
                    alt={media.alt || `Product photo ${index + 1}`}
                  />
                )}
                <button
                  type="button"
                  className="admin-danger-link"
                  onClick={() => removeMedia(index)}
                >
                  Remove {media.type === "video" ? "video" : "photo"}
                </button>
              </fieldset>
            ))}

            {form.media.length === 0 && (
              <p className="admin-empty-copy">No additional media added.</p>
            )}
          </section>

          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={form.featured}
              onChange={(event) =>
                updateField("featured", event.target.checked)
              }
            />
            <span>Feature this product on the storefront</span>
          </label>

          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={form.inStock}
              onChange={(event) =>
                updateField("inStock", event.target.checked)
              }
            />
            <span>Product is in stock</span>
          </label>

          <div className="admin-form-actions">
            <button
              type="submit"
              disabled={saving || uploadingMain || uploadingMediaIndex !== null}
            >
              {saving
                ? "Saving…"
                : editingSlug
                  ? "Update product"
                  : "Create product"}
            </button>
            <button
              type="button"
              className="admin-secondary"
              onClick={closeForm}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="admin-product-grid">
        {products.map((product) => (
          <article className="admin-product-card" key={product.id}>
            <div className="admin-product-image">
              <img
                src={resolveProductMediaUrl(product.imageUrl)}
                alt={product.alt}
              />
              {product.featured && <span>Featured</span>}
              {product.inStock === false && (
                <span className="admin-stock-badge">Out of stock</span>
              )}
            </div>
            <div className="admin-product-card-body">
              <header>
                <div>
                  <h3>{product.name}</h3>
                  <small>{product.id}</small>
                </div>
                <strong>
                  {new Intl.NumberFormat("en", {
                    style: "currency",
                    currency: product.currency,
                  }).format(product.price)}
                </strong>
              </header>
              <p>{product.description}</p>
              <dl>
                <div>
                  <dt>Type</dt>
                  <dd>
                    {(product.productType || "balloon") === "candle"
                      ? "Candle set"
                      : "Balloon"}
                  </dd>
                </div>
                <div>
                  <dt>Color</dt>
                  <dd>
                    <i style={{ backgroundColor: product.color.value }} />
                    {product.color.name}
                  </dd>
                </div>
                {product.secondColor && (
                  <div>
                    <dt>Second color</dt>
                    <dd>
                      <i
                        style={{ backgroundColor: product.secondColor.value }}
                      />
                      {product.secondColor.name}
                    </dd>
                  </div>
                )}
                <div>
                  <dt>Fragrance note</dt>
                  <dd>{product.fragranceNote || "Not set"}</dd>
                </div>
                <div>
                  <dt>Label</dt>
                  <dd>{product.collectionLabel || "None"}</dd>
                </div>
                <div>
                  <dt>Media</dt>
                  <dd>{product.media.length} additional item(s)</dd>
                </div>
              </dl>
              <div className="admin-product-actions">
                <button type="button" onClick={() => startEdit(product)}>
                  Edit
                </button>
                <button
                  type="button"
                  className="admin-delete"
                  disabled={deletingSlug === product.id}
                  onClick={() => handleDelete(product)}
                >
                  {deletingSlug === product.id ? "Deleting…" : "Delete"}
                </button>
              </div>
            </div>
          </article>
        ))}

        {products.length === 0 && (
          <p className="admin-empty-copy">
            No products yet. Add the first one.
          </p>
        )}
      </div>
    </section>
  );
}

export default ProductManager;
